import { Alert, Checkbox, Text } from '@mantine/core'
import { IconAlertTriangle } from '@tabler/icons-react'
import type { FaxPatientCheck } from '@/hooks/useFaxPatientCheck'
import { formatPages } from './formatPages'

type CheckedGroup = FaxPatientCheck['groups'][number]

// The pages of one group that name someone other than its chart (PD-2049), one line each, and
// those that name no patient at all (PD-2055).
export function FaxPatientWarnings({ group }: { group: CheckedGroup | undefined }) {
  if (!group || (group.mismatches.length === 0 && group.unnamedPages.length === 0)) return null
  const chart = withDob(group.chart.display, group.chart.dob)
  const different = group.mismatches.length > 0
  const oneUnnamed = group.unnamedPages.length === 1
  return (
    <Alert
      color={different ? 'red' : 'orange'}
      p="sm"
      icon={<IconAlertTriangle size={16} />}
      title={different ? 'This fax names a different patient' : 'Check these pages before filing'}
    >
      {group.mismatches.map((mismatch) => (
        <Text key={mismatch.pages.join(',')} size="sm">
          {formatPages(mismatch.pages)} {mismatch.pages.length === 1 ? 'names' : 'name'}{' '}
          {withDob(mismatch.name, mismatch.dob)}, not {chart}.
        </Text>
      ))}
      {group.unnamedPages.length > 0 && (
        <Text size="sm">
          {formatPages(group.unnamedPages)} {oneUnnamed ? 'does not' : 'do not'} name a patient —
          check {oneUnnamed ? 'it belongs' : 'they belong'} to {chart}.
        </Text>
      )}
    </Alert>
  )
}

const withDob = (name: string, dob: string | null) => (dob ? `${name} (DOB ${dob})` : name)

// Filing pages the check flagged takes an explicit confirmation, which is recorded (PD-2053).
export function FaxPatientReviewConfirm({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
}) {
  return (
    <Checkbox
      color="red"
      label="I've checked the flagged pages and want to file them as shown"
      checked={checked}
      onChange={(e) => onChange(e.currentTarget.checked)}
      disabled={disabled}
    />
  )
}
